// Overview — GET /api/office/overview, one Stat per figure, each a link to the
// screen that acts on it. Money blocks come back null without financials.read
// and render "—" rather than a zero that would look like a fact.
//
// ABOVE the money: LiveCrew, who is on the clock right now. The owner logged in
// as admin and could not see their own business — "It'd be nice to have the
// admin dashboard to see a high level overview of useful things! including
// who's actively working" — and this screen showed four money blocks and
// nothing about a crew standing in a yard. See LiveCrew's own header for the
// endpoint, the gate and why the numbers are not derived here.
//
// Below the stat grid: OverviewInsight, the weekly hours chart and the top-jobs
// table off /api/dashboard/weekly-chart and /api/dashboard/top-jobs. They live
// in ../insight/ with their own queries — see that file's header for the
// contract and for why they are not inlined here.
//
// Then the quick actions — docs/REBUILD_PARITY.md row "Quick actions". See
// QUICK_ACTIONS below for what each one is gated on and why.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stat, SectionLabel, Notice, PersonRow, Skeleton, BigClock, initialsOf } from '../../../ui/fw';
import { Icon } from '../../../ui';
import { get, describeError } from '../../shared/http';
import { fetchLiveClock, elapsedNow } from '../../shared/liveClock';
import { useCan, useRole } from '../../../hooks/usePermission';
import { useAuth } from '../../../App';
import { destinationsFor, keyForPath } from '../../shared/navigation';
import { fmtMoney, fmtHours, fmtDate } from '../shared/format';
import { PageHeader, LoadFailed, Loading } from '../shared/Page';
import OverviewInsight from '../insight/OverviewInsight';
import './Overview.css';

/**
 * Every nav key this person can actually reach.
 *
 * THE ONE NAV TABLE IS THE AUTHORITY, the same way canSeeLiveCrew below reads
 * it and apps/shell/badges.js reads it for the Approve badge. Merged
 * permissions, never the role label — a promoted lead reaches /approve on a
 * grant, and their stats must link there.
 */
function useReachable() {
  const role = useRole();
  const { user } = useAuth();
  const permissions = user && user.permissions;
  return React.useMemo(
    () => new Set(destinationsFor(role, { permissions }).map((d) => d.key)),
    [role, permissions],
  );
}

/**
 * One figure, linked to the screen that acts on it — WHEN this person can open
 * that screen. Otherwise the same figure, as a number.
 *
 * ── Why the fallback exists ─────────────────────────────────────────────────
 *
 * This screen's shape is "a Stat per figure, each a link", and until
 * 2026-09-10 that link was unconditional. Measured in a browser as a real
 * `accounting` user: four of their thirteen stat links landed on
 *
 *   "Not part of your app — This screen is not one your role uses."
 *
 * `Unapproved clock hours` -> /approve, and `No client` / `Blocked` / `Open`
 * -> /board, three times. server/database.js seeds accounting
 * `timesheets: ['read','read_all']`; `read_all` is a MARKER, not approve, so
 * navigation.js gives them neither row and AppRouter's `Allowed` refuses both
 * URLs. On the FIRST screen a bookkeeper sees, four numbers were a promise the
 * app could not keep.
 *
 * A dead link is worse than a plain number; a deleted figure is worse than
 * both. A job with no client on it cannot be BILLED, which makes that number
 * the bookkeeper's business even though the board behind it is not — so the
 * figure stays and only the link goes.
 */
function StatLink({ to, label, value, tone, hint, linked = true }) {
  const reachable = useReachable();
  // Strip the query: keyForPath answers about a PATH, and every money stat
  // here carries a `?status=` filter. Asking it about the whole href returns
  // null, which would silently unlink the screens that do work.
  const key = keyForPath(String(to || '').split('?')[0]);
  // `linked={false}`: the screen opens, but answers a narrower question than
  // the figure (see the Jobs tiles), which is the same broken promise.
  if (!linked || !key || !reachable.has(key)) {
    return <Stat data-stat={label} label={label} value={value} tone={tone} hint={hint} />;
  }
  return (
    <Link to={to} className="of-statlink" data-stat={label}>
      <Stat label={label} value={value} tone={tone} hint={hint} />
    </Link>
  );
}

const count = (n) => (n === null || n === undefined ? '—' : String(n));

/**
 * How much of today's work has nobody on it.
 *
 * ── Derived here, and why that is allowed ───────────────────────────────────
 *
 * `GET /api/dispatch/now` sends `counts` = { on_clock, late, due, finished }
 * and there is no `unassigned` among them. This counts rows the server
 * already sent rather than asking for a new aggregate: `due_not_started[]`
 * carries one entry per job due today that nobody has clocked into, and each
 * entry carries `lead_user_id` (server/routes/dispatch.js shapeJob).
 *
 * That is the same column the crew board GROUPS on — `if (!r.user_id)
 * unassigned.push(...)` in the board handler — so this figure and the
 * board's Unassigned rail can never disagree about a job. Reading
 * `assignees` instead would have been wrong: `PATCH /api/jobs/:id/schedule`
 * updates `jobs.user_id` and never touches `job_assignees`, so that array
 * goes stale the moment somebody reassigns from the board.
 *
 * This is a COUNT of rows, not arithmetic over money, and the distinction is
 * the one CLAUDE.md draws: no figure here is computed from parts the server
 * did not send.
 *
 * `null` — never 0 — when there is nothing to count from, because "no jobs
 * are unassigned" and "we could not ask" are different answers and only the
 * first is good news.
 */
export function unassignedToday(dueNotStarted) {
  if (!Array.isArray(dueNotStarted)) return null;
  return dueNotStarted.filter((j) => j && j.lead_user_id == null).length;
}

/**
 * Where somebody is, in the Right now screen's own words: the property, then
 * the job. `placeOf` in apps/dispatch/screens/Now.js is this line, and the
 * wording is deliberately identical — the same crew member must not read as
 * "Kaplan · Hedge row" on one screen and something else on another. It is
 * copied rather than imported because Office importing from Dispatch is the
 * app-to-app coupling apps/dispatch/lib/format.js refuses in its own footer.
 */
const placeOf = (r) => [r.property_name, r.job_title].filter(Boolean).join(' · ') || 'No job';

/**
 * May this person load the live view at all?
 *
 * THE ONE NAV TABLE IS THE AUTHORITY, exactly as apps/shell/badges.js does it
 * for the Approve badge, and for the same reason: the rule already exists in
 * three places and a fourth copy would disagree with all three.
 *
 * The disagreement here is real and it is not hypothetical:
 *
 *   · `overview` is ALL_STAFF (shared/navigation.js:114) — manager, accounting
 *     and admin all land on this screen.
 *   · `/api/dispatch/now` is `requireDispatch` (server/routes/dispatch.js:45),
 *     i.e. `canDispatch` at :39-44 — admin, or `timesheets` containing
 *     approve / manage_team / manage_all.
 *   · accounting is seeded `timesheets: ['read', 'read_all']`
 *     (server/database.js:1053) and `read_all` is a MARKER, not a wildcard. So
 *     a bookkeeper opening Overview would fire a request the server answers
 *     403 — the exact bug this repo shipped three times in one week.
 *
 * The `now` destination carries `roles: DISPATCHERS` plus
 * `needs: ['timesheets', 'approve']`, which admits whoever canDispatch admits
 * (a promoted lead included) and nobody else. Cases in Overview.test.js read
 * canDispatch out of dispatch.js and fail if
 * the two drift, and they assert the direction that matters: this gate may be
 * narrower than the server's, never wider.
 *
 * Exported so the gate can be answered per role without rendering anything.
 */
export function canSeeLiveCrew(role, permissions) {
  return destinationsFor(role, { permissions }).some((d) => d.key === 'now');
}

/**
 * Who is on the clock right now — the FIRST thing on the screen.
 *
 * ── The endpoint, and why this one ──────────────────────────────────────────
 *
 * `GET /api/dispatch/now` (server/routes/dispatch.js:78). It already answers
 * this exact question and nothing else here had to be built server-side:
 * `clocked_in[]` carries { user_id, name, initials, job_id, job_title,
 * property_name, started_at, elapsed_seconds, lat, lng } and `counts` carries
 * { on_clock, late, due, finished }. No new aggregate, no new field.
 *
 * The alternative was /api/dashboard/stats, which has `clockedInCount` and
 * `totalWorkers` — a NUMBER and no names. "3 people are working" is not an
 * answer to "who's actively working", and it is the shape the classic
 * dashboard had that put the owner here in the first place.
 *
 * ── Its own query, so a broken panel breaks only itself ─────────────────────
 *
 * Same reasoning as insight/OverviewInsight.js: LiveCrew renders in all three
 * of Overview's branches, so a 500 on /office/overview no longer takes the
 * crew off the screen. An admin who cannot see their money must still be able
 * to see their people.
 *
 * ── It ticks now, and half of why it did not still holds (2026-09-10) ───────
 *
 * This paragraph read "No local per-second tick, deliberately", and its
 * argument was sound at the time: `formatClock` renders H:MM, so a 60s
 * refetch is already the resolution of the thing displayed and a 1s interval
 * would re-render this card 59 times to change nothing.
 *
 * The owner's instruction inverts that arithmetic — "Timer running should
 * show seconds, so we can see timer is actually running." At second
 * resolution the render changes the screen every time, and a figure that
 * never moves on a card about what is happening right now reads as a page
 * that has died. (This sentence named the card's heading until 2026-09-11,
 * when the card became "Today" and "On the clock now" moved down onto the
 * roster. The argument is about the ticking clock, not about the wording.)
 * So the tick is on, gated on somebody actually BEING on the clock, so a
 * dashboard left open on a quiet afternoon runs no timer at all.
 *
 * What is NOT reversed is the reason this refetches at 60s rather than the
 * 30s Now.js uses: server/server.js rate-limits /api/ to 1000 requests per 15
 * minutes PER IP, a crew shares one, and a dashboard is the screen most
 * likely to be left open in a tab all day (apps/shell/badges.js). The
 * interval below is a BROWSER tick over a figure the answer already carried;
 * it asks the server for nothing. react-query still does not refetch while
 * the tab is hidden, and refetchOnWindowFocus still makes it immediate the
 * moment somebody looks.
 *
 * `fetchLiveClock` rather than a bare `get`, for the same reason Now.js uses
 * it: it stamps `received_at` onto the answer, which is what lets `elapsedNow`
 * add only the time since the answer landed. A dashboard whose clock counted
 * from the DEVICE's idea of the start would disagree with the Right now
 * screen on a laptop whose clock has drifted.
 *
 * ── The one collapse this must not make ─────────────────────────────────────
 *
 * "Nobody is on the clock" and "the request did not answer" are DIFFERENT
 * facts and the first is the one an owner acts on. `q.data.clocked_in` is read
 * as an array or as nothing — never `|| []` — so a refusal, and a 200 whose
 * shape we cannot read, print a sentence instead of a roster of nobody.
 */
function LiveCrew() {
  const role = useRole();
  // The merged set (role baseline ∪ extra_permissions) that /api/auth/me
  // returns, not the role label — a promoted lead reaches /now on the grant.
  const { user } = useAuth();
  const allowed = canSeeLiveCrew(role, user && user.permissions);

  const q = useQuery({
    // Overview's own key, NOT ['dispatch', 'now']. The two screens refetch on
    // different intervals — 60s here, 30s there, for the rate-limit reason
    // above — and one cache entry cannot hold two schedules.
    queryKey: ['office', 'overview', 'live-crew'],
    queryFn: fetchLiveClock,
    enabled: allowed,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const rows = q.data && Array.isArray(q.data.clocked_in) ? q.data.clocked_in : null;
  // ONE interval for the whole card, and none while nobody is on the clock.
  const live = rows ? rows.length : 0;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);

  // After the hooks, never before them.
  if (!allowed) return null;

  const counts = (q.data && q.data.counts) || {};
  // A refusal, or a 200 nobody can read. Either way it is not "nobody".
  const problem = !q.isPending && !rows;
  // Read as an array or as nothing, exactly as `rows` is — a refusal must not
  // be able to render as "0 jobs are unassigned".
  const due = q.data && Array.isArray(q.data.due_not_started) ? q.data.due_not_started : null;
  const unassigned = unassignedToday(due);

  return (
    <section className="of-card" data-panel="live-crew">
      <div className="of-card__head">
        {/*
          "Today", not "On site now".

          The card grew from three figures about the clock to five about the
          day (see the stat row below), and a heading naming only the clock
          would have been the phone-header bug this app already fixed once —
          a section called one thing over content that is another.
          "On the clock now" moved down onto the roster, which is what it
          actually describes.
        */}
        <SectionLabel>Today</SectionLabel>
        {/* One link, not five identical ones. The two figures about work
            nobody has picked up carry their OWN link, because the screen that
            acts on them is the board and not the live view — see the comment
            on the stat row. */}
        <Link className="of-link" to="/now" data-live-more>Right now</Link>
      </div>
      {q.isPending ? <Skeleton lines={4} /> : null}
      {problem ? (
        <Notice tone="stop" data-panel-error="live-crew">
          {describeError(q.error, 'Could not load who is on the clock.')}
        </Notice>
      ) : null}
      {rows ? (
        <>
          {/*
            ── The day's work, which this screen never showed ────────────────
            //
            Measured at 320px as admin on 2026-09-11, before this change: the
            first viewport of the Overview answered ONE of the five questions
            an admin opens it to ask. On clock, Late and Finished were the
            whole of it; "what is scheduled today" and "what has nobody on it"
            appeared nowhere on a 4133px page. `Open = 3` at y=2100 is every
            open job ever, not today's.

            Both new figures were already in this card's OWN response and were
            being thrown away: `counts.due` is jobs due today that nobody has
            started, and `due_not_started[]` is those same jobs with a
            `lead_user_id` on each. No new endpoint, no new aggregate — the
            recurring defect in this repo is the server returning something the
            screen never draws, and this was two more of it.

            There is deliberately NO "scheduled today" total. The response
            cannot produce one honestly: `due_not_started` excludes anything
            already started and `finished_today` is grouped per user AND job,
            so any total assembled from them would double-count. "Not started"
            is the half that needs somebody to act, and its label says exactly
            what it counts.

            The two work figures LINK, and to /board rather than /now, because
            assigning a job is something you do on the board. StatLink drops
            the link (keeping the number) for anyone whose navigation does not
            carry that destination, which is the fix a bookkeeper's four dead
            links bought on 2026-09-10.

            `of-today__stats`, not `of-stats__row`: the shared row floors its
            tracks at 180px, so five figures at 320px would be five full-width
            tiles and would push the roster — the answer to "who is working" —
            another 170px down the page. The narrower floor puts five in the
            same height the old three occupied. See Overview.css.
          */}
          <div className="of-stats__row of-today__stats">
            {/* `rows.length`, not counts.on_clock. They are the same number
                server-side (dispatch.js:132) and a headline figure that can
                disagree with the list directly under it is worse than either. */}
            <Stat data-stat="On clock" label="On clock" value={rows.length} tone={rows.length > 0 ? 'now' : 'neutral'} />
            <StatLink to="/board" label="Not started" value={count(counts.due)} tone={Number(counts.due) > 0 ? 'sched' : 'neutral'} />
            <StatLink to="/board" label="Unassigned" value={count(unassigned)} tone={Number(unassigned) > 0 ? 'warn' : 'neutral'} />
            <Stat data-stat="Late" label="Late" value={count(counts.late)} tone={Number(counts.late) > 0 ? 'stop' : 'neutral'} />
            <Stat data-stat="Finished" label="Finished" value={count(counts.finished)} tone="ok" />
          </div>
          {/* The heading this card used to carry, now on the thing it names. */}
          <SectionLabel>On the clock now</SectionLabel>
          <div data-section="on-clock">
            {rows.length === 0 ? (
              <p className="of-muted">Nobody is on the clock right now.</p>
            ) : null}
            {rows.map((p) => (
              <PersonRow
                key={p.entry_id || `${p.user_id}-${p.job_id}`}
                data-person={p.user_id}
                tone="now"
                initials={p.initials || initialsOf(p.name)}
                name={p.name || 'Unknown worker'}
                meta={placeOf(p)}
                trailing={<BigClock size="sm" seconds={elapsedNow(p.elapsed_seconds, q.data && q.data.received_at, now)} />}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}

/**
 * The shortcuts the classic Dashboard has and Office did not.
 *
 * Every entry is something the signed-in person can COMPLETE, not just a
 * screen they can open — the stat grid above is already a link to every list.
 * Three conditions have to hold before one is offered, and each of them is a
 * real failure that was found by checking rather than assumed:
 *
 *   `nav`   the destination's own navigation.js entry must be listed for this
 *           role. A quick action pointing at a screen missing from someone's
 *           menu is the four-tabs-and-no-way-back bug in miniature.
 *   `can`   every [resource, action] pair the DESTINATION SCREEN gates itself
 *           on, in the same two-argument form it uses. Offering more than the
 *           screen does means landing on a page with the button hidden.
 *   `roles` an extra ROLE list, only where the API the destination calls on
 *           arrival is gated by checkRole rather than by a permission. That is
 *           not hypothetical: POST /api/invoices/from-jobs — the write both
 *           invoice shortcuts end in — is checkRole('admin','manager')
 *           (server/routes/invoices.js), so `accounting`, who holds the whole
 *           invoices permission set, may not complete either one.
 *           OfficeActivity/Overview tests read that handler and fail if the
 *           list moves.
 *
 *           The READ those screens do on arrival, GET /available-jobs, stopped
 *           being a role gate on 2026-09-09: it asks the permission model, so
 *           an office user handed invoice rights through extra_permissions is
 *           served rather than 403'd. That is why `roles` here is read off the
 *           write and not off the picker — the picker no longer has a role
 *           list to read, and the write is what decides whether the shortcut
 *           can be finished at all.
 *
 * "New job" IS here, and the reason it nearly was not is worth keeping. Both
 * of the rebuild's New-job buttons (records/Jobs.js, screens/Schedule.js) were
 * gated on `useCan('jobs.create')` — one argument, so it asked for a RESOURCE
 * named 'jobs.create', which no role has. Only the admin shortcut inside
 * useCan passed it, so a MANAGER saw no New job, Edit, Delete, status change
 * or Mark blocked, and could not schedule a job — while the server allowed
 * every one of them. Sending a manager to a screen whose create button is
 * hidden would have been worse than not offering the shortcut, so this was
 * first reported rather than offered; the gates were then corrected to
 * `useCan('jobs', 'create')` and the shortcut can be honest.
 */
export const QUICK_ACTIONS = [
  {
    key: 'new-job',
    label: 'New job',
    icon: 'jobs',
    to: '/jobs?new=1',
    nav: 'jobs',
    can: [['jobs', 'read'], ['jobs', 'create']],
  },
  {
    key: 'new-estimate',
    label: 'New estimate',
    icon: 'estimates',
    to: '/estimates/new',
    nav: 'estimates',
    can: [['estimates', 'read'], ['estimates', 'create']],
  },
  // Both invoice actions are gated on the ROLE ALONE, with no `can` pair.
  // Every route in server/routes/invoices.js these two shortcuts WRITE
  // through is checkRole(...), so a permission is the wrong question to ask
  // about them — and asking it got both answers wrong at once: a MANAGER
  // holds no `invoices` permission and every one of those routes serves them,
  // an ACCOUNTING user holds all six and they refuse. See the header of
  // shared/moneyEndpoints.js. (GET /available-jobs, the READ on arrival, is
  // permission-gated since 2026-09-09 — it is not what decides whether these
  // shortcuts can be completed.)
  {
    key: 'new-invoice',
    label: 'New invoice',
    icon: 'billing',
    to: '/invoices/new',
    nav: 'invoices',
    can: [],
    roles: ['admin', 'manager'],
  },
  {
    key: 'bill-jobs',
    label: 'Bill finished jobs',
    icon: 'jobs',
    to: '/invoices/from-jobs',
    nav: 'invoices',
    can: [],
    roles: ['admin', 'manager'],
  },
  {
    key: 'new-client',
    label: 'New client',
    icon: 'clients',
    to: '/clients',
    nav: 'clients',
    can: [['clients', 'read'], ['clients', 'create']],
  },
];

/**
 * Which quick actions this person may actually complete. Pure, so the gating
 * is testable per role without rendering anything.
 *
 * @param role  useRole()
 * @param can   (resource, action) => boolean — useCan, curried by the caller
 */
export function quickActionsFor(role, can) {
  // No second argument. `{ app: 'office' }` was passed here until 2026-09-07 —
  // a leftover from the three-app cutover, ignored then and ignored now. It
  // matters that it is gone: that parameter carries `{ permissions }` since
  // this session, and a stale key sitting in the slot reads as if this call
  // were permission-aware when it is not. Overview is an ALL_STAFF screen, so
  // nobody reaches it on a grant alone and role is the whole question here.
  const reachable = new Set(destinationsFor(role).map((d) => d.key));
  return QUICK_ACTIONS.filter((a) => (
    reachable.has(a.nav)
    && (!a.roles || a.roles.includes(role))
    && a.can.every(([resource, action]) => can(resource, action))
  ));
}

function QuickActions() {
  const role = useRole();
  // One useCan per pair, unconditionally, because they are hooks. The map is
  // what `quickActionsFor` reads; a pair missing from it reads as `undefined
  // === true` → false, and the action silently vanishes for EVERYBODY.
  //
  // That is not hypothetical. `jobs:read` and `jobs:create` were missing until
  // 2026-09-07, so the `new-job` quick action — which declares exactly those
  // two — was filtered out for admin, manager and accounting alike. Measured
  // off the live DOM: admin and manager got new-estimate, new-invoice,
  // bill-jobs, new-client; accounting got new-estimate, new-client. Nobody
  // ever got New job.
  //
  // The comment here claimed "Overview.test.js asserts every pair in
  // QUICK_ACTIONS has one". It did not: that test passes a SYNTHETIC `can`
  // into quickActionsFor and never mounts this component, so the map it is
  // supposedly guarding is not in the picture at all. A test that drives the
  // pure function can never see a gap in its caller's inputs. Overview.test.js
  // now renders the component and looks for the action.
  const answers = {
    'estimates:read': useCan('estimates', 'read'),
    'estimates:create': useCan('estimates', 'create'),
    'clients:read': useCan('clients', 'read'),
    'clients:create': useCan('clients', 'create'),
    'jobs:read': useCan('jobs', 'read'),
    'jobs:create': useCan('jobs', 'create'),
  };
  const actions = quickActionsFor(role, (resource, action) => answers[`${resource}:${action}`] === true);

  // A role with none of them gets no card at all, rather than an empty one
  // headed "Quick actions" — which would read as a broken panel.
  if (actions.length === 0) return null;

  return (
    <section className="of-card" data-panel="quick-actions">
      <div className="of-card__head">
        <SectionLabel>Quick actions</SectionLabel>
      </div>
      {/* of-inline wraps, so this row cannot push a 390px page sideways. */}
      <div className="of-inline">
        {/*
          `primary`, not `ghost`, since 2026-09-10.

          All five were outlines, which is how every creation action in the
          product came to be the least emphatic thing on the screen a manager
          lands on. It also meant ONE action had TWO weights depending on which
          screen offered it: records/Jobs.js draws New job as
          `variant="primary"`, and the same words here were a bordered
          transparent box sitting among Stat tiles that are also bordered
          transparent boxes.

          Every entry in this card is something the signed-in person can
          COMPLETE — quickActionsFor has already dropped the ones they cannot —
          so there is nothing here for a filled button to out-shout.
        */}
        {actions.map((a) => (
          <Link key={a.key} to={a.to} className="fw-btn fw-btn--primary fw-btn--md" data-quick={a.key}>
            <Icon name={a.icon} className="fw-btn__icon" />
            <span className="fw-btn__label">{a.label}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Overview() {
  const q = useQuery({ queryKey: ['office', 'overview'], queryFn: () => get('/office/overview') });
  // The Jobs tiles count the whole company; GET /api/jobs answers a role
  // without job oversight with its own and unassigned jobs only (routes/
  // jobs.js hasJobOversight). For that role the list behind the tile would
  // say less than the tile, so the tile stays a number (review of #738).
  const allJobs = useCan('jobs', 'manage_all');
  const teamJobs = useCan('jobs', 'manage_team');
  const jobOversight = allJobs || teamJobs;

  // LiveCrew is in all three branches, and that is the point: the crew is the
  // one thing on this screen that must survive /office/overview failing. It
  // owns its own request, so it is a card either way rather than a hole.
  if (q.isPending) return <><PageHeader title="Overview" /><LiveCrew /><Loading lines={6} /></>;
  if (q.isError) return <><PageHeader title="Overview" /><LiveCrew /><LoadFailed error={q.error} onRetry={() => q.refetch()} what="the overview" /></>;

  const d = q.data || {};
  const inv = d.invoices;
  const est = d.estimates;
  const pay = d.payroll || {};
  const jobs = d.jobs || {};
  const money = (v) => fmtMoney(v);
  /**
   * "As of Sep 10, 2026", not "As of 2026-09-10".
   *
   * `d.today` is the DATE column as the server stores it, and this line handed
   * it straight to a person until 2026-09-10 — the first date on the first
   * screen of the day, and the one date in the app written for a database.
   * fmtDate is what every other Office date already goes through, and it parses
   * at local noon so the day is never one out west of Greenwich
   * (dateOnlyBoundaries.test.js).
   *
   * `'—'` is fmtDate's answer for anything that is not a date, and "As of —"
   * says less than nothing — so the meta is dropped entirely in that case,
   * which is the shape this line already had for a missing `today`.
   */
  const asOf = d.today ? fmtDate(d.today) : '—';

  // What the server withheld, named exactly.
  //
  // This was one fixed sentence — "Invoice and estimate figures need the
  // financials permission" — and it stopped being true on 2026-09-10, when
  // the estimate COUNTS moved off `financials.read` and onto estimate access
  // (server/routes/office.js). A manager now gets the counts and no invoice
  // block, so the old sentence sat above three estimate figures explaining
  // that estimate figures are not shown: a screen arguing with itself, which
  // is exactly the "what am I looking at" complaint this run is about.
  //
  // The estimate block can still be withheld — a role that cannot read
  // estimates at all — so all three cases are said, each in its own words.
  const withheld = (!inv && !est)
    ? 'Invoice figures need the financials permission and estimate figures need access to estimates, so neither is shown here for you.'
    : (!inv
      ? 'Invoice figures need the financials permission, so they are not shown here for you.'
      : (!est ? 'Estimate figures need access to estimates, so they are not shown here for you.' : null));

  return (
    <>
      <PageHeader title="Overview" meta={asOf === '—' ? null : `As of ${asOf}`} />
      {/* First, before the money. The owner's complaint was that they log in
          and cannot see their business; a crew in a yard is the business. */}
      <LiveCrew />
      {/*
        The five things a person can MAKE, above the sixteen figures they can
        look at — moved here 2026-09-10.

        Measured at 390px before the move: the quick actions sat at y=1901–2125
        of a 4136px page, behind 1274px of stat grid, on the screen a manager
        and a bookkeeper land on. Nothing above them was a thing you complete;
        the stats are all links to somewhere else. LiveCrew stays first, which
        is the one block on this screen whose position the owner asked for.
      */}
      <QuickActions />
      {/*
        A block the server WITHHELD is not drawn at all, since 2026-09-10.
        It used to render its figures as "—", which was right about the one
        thing that matters — a withheld number must never look like a zero —
        and wrong about everything else. Measured in a browser at 320px and
        390px as a real `manager`, whose seeded matrix holds neither
        `financials` nor `invoices`: SEVEN of their sixteen stats were dashes,
        so the first screen of their day was mostly a list of things they are
        not allowed to know, pushing the three figures that ARE theirs below
        them. The Notice says it once; seven dashes said it seven more times.
      */}
      {withheld ? <Notice tone="info">{withheld}</Notice> : null}
      <div className="of-stats">
        {inv ? (
          <section>
            <SectionLabel>Invoices</SectionLabel>
            <div className="of-stats__row">
              <StatLink to="/invoices?status=outstanding" label="Outstanding" value={money(inv.outstanding)} />
              <StatLink to="/invoices?status=overdue" label="Overdue" value={money(inv.overdue)} tone={Number(inv.overdue) > 0 ? 'stop' : 'neutral'} hint={inv.overdue_count ? `${inv.overdue_count} past due` : undefined} />
              <StatLink to="/invoices?status=paid" label="Paid this month" value={money(inv.paid_this_month)} tone="ok" />
              <StatLink to="/invoices?status=draft" label="Drafts" value={count(inv.drafts)} />
            </div>
          </section>
        ) : null}
        {est ? (
          <section>
            <SectionLabel>Estimates</SectionLabel>
            <div className="of-stats__row">
              <StatLink to="/estimates?status=pending_approval" label="Awaiting approval" value={count(est.awaiting_approval)} tone={Number(est.awaiting_approval) > 0 ? 'warn' : 'neutral'} />
              <StatLink to="/estimates?status=sent" label="Sent" value={count(est.sent)} hint={est.sent_value ? `${fmtMoney(est.sent_value)} out` : undefined} />
              <StatLink to="/estimates?status=accepted" label="Accepted this month" value={count(est.accepted_this_month)} tone="ok" />
            </div>
          </section>
        ) : null}
        <section>
          <SectionLabel>Payroll</SectionLabel>
          <div className="of-stats__row">
            <StatLink to="/payroll" label="To approve" value={count(pay.to_approve)} tone={Number(pay.to_approve) > 0 ? 'warn' : 'neutral'} />
            <StatLink to="/approve" label="Unapproved clock hours" value={pay.unapproved_clock_hours === undefined ? '—' : fmtHours(pay.unapproved_clock_hours)} tone={Number(pay.unapproved_clock_hours) > 0 ? 'warn' : 'neutral'} hint="this week" />
            <StatLink to="/payroll?status=pending" label="Paystubs pending" value={count(pay.paystubs_pending)} />
          </div>
        </section>
        <section>
          <SectionLabel>Jobs</SectionLabel>
          <div className="of-stats__row">
            {/* Each opens the list it counts (routes/office.js). They all
                opened /board, which shows today and tomorrow only. */}
            <StatLink to="/jobs?client=none&status=open" linked={jobOversight} label="No client" value={count(jobs.no_client)} tone={Number(jobs.no_client) > 0 ? 'warn' : 'neutral'} />
            <StatLink to="/jobs?status=blocked" linked={jobOversight} label="Blocked" value={count(jobs.blocked)} tone={Number(jobs.blocked) > 0 ? 'stop' : 'neutral'} />
            <StatLink to="/jobs?status=open" linked={jobOversight} label="Open" value={count(jobs.open)} />
          </div>
        </section>
      </div>
      {/*
        The two Dashboard panels REBUILD_PARITY lists as missing. They own their
        own requests, so a refused /dashboard/weekly-chart shows its own
        sentence instead of taking the stat grid down with it.
      */}
      <OverviewInsight />
    </>
  );
}
